/* eslint-disable */
import { msg } from '@lingui/macro'

import {
  AliasedExpressions,
  DomainBuilder,
  DomainComponentOpticBuilder,
  InvariantBuilder,
  RecipeFactoryBuilder,
  createConstantExpressionFactory,
  createCustomExpressionFactory,
  createCustomInvariantFactory,
  createRelationFactory,
} from '@salsita-ai/platform-project-sdk/domain'

import { BackendData, FrontendData, PublishedData, SessionData } from './application-types.js'
import { $Map, $entityReflections, constants } from './enhanced-model.js'
import { Aliases, AnyDomainEntity, RootEntity, aliases, entities, modelVersion } from './model/index.js'

const implementRelation = createRelationFactory<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(aliases)
export const relationExpressions = {
  Table: {
    $top: implementRelation<'Table', 'Top'>({
      kind: 'direct',
      target: 'Top',
      field: 'topId',
      cardinality: '1-1',
    }),
    $base: implementRelation<'Table', 'Base'>({
      kind: 'direct',
      target: 'Base',
      field: 'baseStyleId',
      cardinality: '1-1',
    }),
    $bench: implementRelation<'Table', 'Bench'>({
      kind: 'direct',
      target: 'Bench',
      field: 'benchId',
      cardinality: '1-1',
    }),
  },
  Top: {
    $table: implementRelation<'Top', 'Table'>({
      kind: 'indirect',
      target: 'Table',
      field: 'topId',
      cardinality: '1-1',
    }),
    $apron: implementRelation<'Top', 'Apron'>({
      kind: 'direct',
      target: 'Apron',
      field: 'apronId',
      cardinality: '1-1',
    }),
  },
  Base: {
    $table: implementRelation<'Base', 'Table'>({
      kind: 'indirect',
      target: 'Table',
      field: 'baseStyleId',
      cardinality: '1-1',
    }),
  },
  Bench: {
    $table: implementRelation<'Bench', 'Table'>({
      kind: 'indirect',
      target: 'Table',
      field: 'benchId',
      cardinality: '1-1',
    }),
  },
  Apron: {
    $top: implementRelation<'Apron', 'Top'>({
      kind: 'indirect',
      target: 'Top',
      field: 'apronId',
      cardinality: '1-1',
    }),
  },
} as const satisfies AliasedExpressions<AnyDomainEntity, RootEntity, PublishedData, SessionData, FrontendData, Aliases>

export const constantExpressions = createConstantExpressionFactory<
  AnyDomainEntity,
  RootEntity,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(
  {} // insert expression metas here, once ready
)(constants)

export const createRecipe = RecipeFactoryBuilder.forAliases<AnyDomainEntity, Aliases>(aliases)
  .forEntities(AnyDomainEntity.reflections)
  .withDefaults({})
  .build()
interface CustomInvariantBlueprints {
  Base: {
    customBaseFinishNeedsPersonalizedQuote: boolean
    baseFourLegMustHaveAprons: boolean
    supportsCrossbrace: boolean
  }
  Table: {
    seatingAmountNotAvailableForThisSeatingType: boolean
  }
  Top: {
    lengthLargerThanWidth: boolean
    baseStyleNotAvailableForThisTop: boolean
    baseTypeMustHaveStyles: boolean
    leafAmountNotAvailableForThisLeafType: boolean
    lengthMightNotBeAvailableForThisTop: boolean
    widthMaybeTooBigForDoor: boolean
    thicknessTypeMightNotBeAvailableForThisTop: boolean
    customTopFinishNeedsPersonalizedQuote: boolean
    customLeafSizeNeedsPersonalizedQuote: boolean
    lengthTooShortForDoubleBase: boolean
    lengthTooShortForPerimeterOrDropInLeaf: boolean
    edgeStyleAvailableForThisTop: boolean
    beveledTopNeverRolled: boolean
    roundedCorners: boolean
  }
}
const staticViolations = {
  Base: {
    customBaseFinishNeedsPersonalizedQuote: {
      type: 'customBaseFinishNeedsPersonalizedQuote',
      severity: 'notice',
      message: msg`Custom base finish`,
    },
    baseFourLegMustHaveAprons: {
      type: 'baseFourLegMustHaveAprons',
      severity: 'fatal',
      message: msg`Four leg bases must have an apron`,
    },
    supportsCrossbrace: {
      type: 'supportsCrossbrace',
      severity: 'fatal',
      message: msg`This Base does not support a crossbrace`,
    },
  },
  Table: {
    seatingAmountNotAvailableForThisSeatingType: {
      type: 'seatingAmountNotAvailableForThisSeatingType',
      severity: 'fatal',
      message: msg`Seating Amount cannot be applied for this Seating Type`,
    },
  },
  Top: {
    lengthLargerThanWidth: {
      type: 'lengthLargerThanWidth',
      severity: 'fatal',
      message: msg`Length cannot be larger than width`,
    },
    baseStyleNotAvailableForThisTop: {
      type: 'baseStyleNotAvailableForThisTop',
      severity: 'fatal',
      message: msg`Base Style cannot be applied for this Top`,
    },
    baseTypeMustHaveStyles: {
      type: 'baseTypeMustHaveStyles',
      severity: 'fatal',
      message: msg`No bases of this type are available for the chosen tabletop`,
    },
    leafAmountNotAvailableForThisLeafType: {
      type: 'leafAmountNotAvailableForThisLeafType',
      severity: 'fatal',
      message: msg`Leaf Amount cannot be applied for this Leaf Type`,
    },
    lengthMightNotBeAvailableForThisTop: {
      type: 'lengthMightNotBeAvailableForThisTop',
      severity: 'notice',
      message: msg`Tabletop Length`,
    },
    widthMaybeTooBigForDoor: {
      type: 'widthMaybeTooBigForDoor',
      severity: 'notice',
      message: msg`The selected width might be too big to fit through a normal door`,
    },
    thicknessTypeMightNotBeAvailableForThisTop: {
      type: 'thicknessTypeMightNotBeAvailableForThisTop',
      severity: 'notice',
      message: msg`Tabletop Thickness`,
    },
    customTopFinishNeedsPersonalizedQuote: {
      type: 'customTopFinishNeedsPersonalizedQuote',
      severity: 'notice',
      message: msg`Custom top finish`,
    },
    customLeafSizeNeedsPersonalizedQuote: {
      type: 'customLeafSizeNeedsPersonalizedQuote',
      severity: 'notice',
      message: msg`Custom leaf size`,
    },
    lengthTooShortForDoubleBase: {
      type: 'lengthTooShortForDoubleBase',
      severity: 'fatal',
      message: msg`Length is too short for a double base`,
    },
    lengthTooShortForPerimeterOrDropInLeaf: {
      type: 'lengthTooShortForPerimeterOrDropInLeaf',
      severity: 'fatal',
      message: msg`Length is too short for a perimeter or drop in leaf`,
    },
    edgeStyleAvailableForThisTop: {
      type: 'edgeStyleAvailableForThisTop',
      severity: 'fatal',
      message: msg`Edge style is not available for this top`,
    },
    beveledTopNeverRolled: {
      type: 'beveledTopNeverRolled',
      severity: 'fatal',
      message: msg`Cannot have a rolled Top with a Beveled Edge Style`,
    },
    roundedCorners: {
      type: 'roundedCorners',
      severity: 'fatal',
      message: msg`Round corners are only available on Clean Edged Rectangle or Square tabletops`,
    },
  },
} as const
export const implementCustomInvariants = createCustomInvariantFactory<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases,
  CustomInvariantBlueprints
>()(staticViolations)

export const invariantBuilder = new InvariantBuilder<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(aliases)

export const attachComponent = DomainComponentOpticBuilder.from<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(aliases)

export const implementCustomExpressions = createCustomExpressionFactory<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(
  {} // TODO pass in meta-data once we have them
)
export const dataExpressions = implementCustomExpressions({})

export const domainBuilder = DomainBuilder.from<
  AnyDomainEntity,
  RootEntity,
  $Map,
  PublishedData,
  SessionData,
  FrontendData,
  Aliases
>(
  entities,
  aliases,
  $entityReflections,
  RootEntity.types,
  modelVersion,
  PublishedData,
  SessionData,
  FrontendData,
  BackendData,
  false
)
  .withExpressions(constantExpressions)
  .withExpressions(relationExpressions)
  .withExpressions(dataExpressions)
